import { supabase } from './supabaseClient';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../config/env';

const MASK = '••••••••';

function looksMasked(value) {
  const s = String(value || '');
  return !s || s.includes('•') || s === MASK;
}

function maskSecret(value) {
  const s = String(value || '');
  if (s.length < 8) return s ? MASK : '';
  return `${s.slice(0, 4)}${MASK}${s.slice(-4)}`;
}

function digitsPhone(raw) {
  return String(raw || '').replace(/\D/g, '');
}

function parseHm(hm) {
  const [h, m] = String(hm || '09:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function istMinutes() {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return ist.getHours() * 60 + ist.getMinutes();
}

function inIstWindow(start, end, now = istMinutes()) {
  const a = parseHm(start);
  const b = parseHm(end);
  if (a === b) return true;
  if (a < b) return now >= a && now < b;
  return now >= a || now < b;
}

function withAgentIds(list) {
  return (Array.isArray(list) ? list : []).map((a, i) => ({
    id: String(a?.id || `agent-${i}`),
    name: String(a?.name || '').trim(),
    phone: digitsPhone(a?.phone),
    always_on: a?.always_on === true,
    start: String(a?.start || '09:00').slice(0, 5),
    end: String(a?.end || '21:00').slice(0, 5),
    enabled: a?.enabled !== false,
  }));
}

export function newTransferAgent() {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `agent-${Date.now()}`;
  return {
    id,
    name: '',
    phone: '',
    always_on: true,
    start: '09:00',
    end: '21:00',
    enabled: true,
  };
}

export function seedTransferAgents(settings) {
  const fromCol = withAgentIds(settings?.transfer_agents);
  if (fromCol.some((a) => a.phone.length >= 10)) return withAgentIds(settings.transfer_agents);
  const fromSetup = withAgentIds(settings?.setup_done?.transfer_agents);
  if (fromSetup.some((a) => a.phone.length >= 10)) return fromSetup;
  const day = digitsPhone(settings?.transfer_day_number);
  const night = digitsPhone(settings?.transfer_night_number);
  const start = String(settings?.transfer_day_start || '09:00').slice(0, 5);
  const end = String(settings?.transfer_day_end || '21:00').slice(0, 5);
  if (day.length >= 10 && night.length >= 10 && day === night) {
    return [{ id: 'seed-24', name: 'Agent', phone: day, always_on: true, start: '00:00', end: '00:00', enabled: true }];
  }
  const agents = [];
  if (day.length >= 10) {
    agents.push({ id: 'seed-a', name: 'Shift 1', phone: day, always_on: false, start, end, enabled: true });
  }
  if (night.length >= 10) {
    agents.push({ id: 'seed-b', name: 'Shift 2', phone: night, always_on: false, start: end, end: start, enabled: true });
  }
  return agents;
}

export function isAgentOnDuty(agent, now = istMinutes()) {
  if (!agent || agent.enabled === false) return false;
  const phone = digitsPhone(agent.phone);
  if (phone.length < 10) return false;
  if (agent.always_on) return true;
  return inIstWindow(agent.start || '00:00', agent.end || '00:00', now);
}

export function pickOnDutyAgent(agents) {
  const list = Array.isArray(agents) ? agents : [];
  return list.find((a) => isAgentOnDuty(a))
    || list.find((a) => a.enabled !== false && digitsPhone(a.phone).length >= 10)
    || null;
}

function normalizeTransferAgents(raw) {
  return withAgentIds(raw).filter((a) => a.phone.length >= 10);
}

function restHeaders() {
  const headers = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };
  try {
    const raw = localStorage.getItem('roadserve_sb_session');
    const backup = raw ? JSON.parse(raw) : null;
    if (backup?.access_token) headers.Authorization = `Bearer ${backup.access_token}`;
  } catch {
    /* anon */
  }
  return headers;
}

export const VoiceAiService = {
  brainUrl() {
    return `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/voice-ai-reply`;
  },

  maskSecret,

  looksMasked,

  async getSettings() {
    const { data, error } = await supabase.from('voice_ai_settings').select('*').eq('id', 1).maybeSingle();
    if (error) throw new Error(error.message || 'Failed to load Voice AI settings');
    const row = data || { id: 1 };
    const setup_done = row.setup_done && typeof row.setup_done === 'object' ? row.setup_done : {};
    return {
      ...row,
      omnidim_api_key_masked: maskSecret(row.omnidim_api_key),
      webhook_secret: row.webhook_secret || '',
      setup_done,
      transfer_agents: seedTransferAgents({ ...row, setup_done }),
    };
  },

  async saveSettings(form) {
    const current = await VoiceAiService.getSettings();
    const agents = normalizeTransferAgents(form.transfer_agents);
    const onDuty = pickOnDutyAgent(agents);
    const always = agents.find((a) => a.always_on);
    const timed = agents.find((a) => !a.always_on);
    const setup_done = {
      ...(form.setup_done && typeof form.setup_done === 'object' ? form.setup_done : current.setup_done || {}),
      transfer_agents: agents,
    };
    const patch = {
      id: 1,
      enabled: !!form.enabled,
      welcome_message: String(form.welcome_message || '').trim() || current.welcome_message,
      omnidim_agent_id: String(form.omnidim_agent_id || '').trim() || null,
      omnidim_from_number_id: String(form.omnidim_from_number_id || '').trim() || null,
      inbound_did: String(form.inbound_did || '').trim() || null,
      wa_account_id: form.wa_account_id || current.wa_account_id || null,
      location_template: String(form.location_template || 'ask_customer_location').trim(),
      confirm_template: String(form.confirm_template || 'complaint_registration_confirmation').trim(),
      transfer_day_number: (timed || onDuty || agents[0] || {}).phone
        || String(form.transfer_day_number || '').replace(/\D/g, '')
        || current.transfer_day_number
        || '7909999427',
      transfer_night_number: (always || onDuty || agents[agents.length - 1] || {}).phone
        || String(form.transfer_night_number || '').replace(/\D/g, '')
        || current.transfer_night_number
        || '18003093431',
      transfer_day_start: (timed || agents[0])?.start || String(form.transfer_day_start || '09:00'),
      transfer_day_end: (timed || agents[0])?.end || String(form.transfer_day_end || '21:00'),
      transfer_agents: agents,
      location_wait_seconds: Math.max(30, Math.min(180, Number(form.location_wait_seconds) || 90)),
      setup_done,
      updated_at: new Date().toISOString(),
    };
    if (!looksMasked(form.omnidim_api_key) && String(form.omnidim_api_key || '').trim()) {
      patch.omnidim_api_key = String(form.omnidim_api_key).trim();
    }
    if (!looksMasked(form.webhook_secret) && String(form.webhook_secret || '').trim().length >= 8) {
      patch.webhook_secret = String(form.webhook_secret).trim();
    }
    const { data, error } = await supabase
      .from('voice_ai_settings')
      .upsert(patch, { onConflict: 'id' })
      .select('*')
      .maybeSingle();
    if (error && /transfer_agents/i.test(error.message || '')) {
      delete patch.transfer_agents;
      const retry = await supabase
        .from('voice_ai_settings')
        .upsert(patch, { onConflict: 'id' })
        .select('*')
        .maybeSingle();
      if (retry.error) throw new Error(retry.error.message || 'Failed to save Voice AI settings');
      return retry.data;
    }
    if (error) throw new Error(error.message || 'Failed to save Voice AI settings');
    return data;
  },

  async rotateWebhookSecret() {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const secret = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
    const { error } = await supabase
      .from('voice_ai_settings')
      .update({ webhook_secret: secret, updated_at: new Date().toISOString() })
      .eq('id', 1);
    if (error) throw new Error(error.message || 'Failed to rotate secret');
    return secret;
  },

  async listSessions(limit = 20) {
    const { data, error } = await supabase
      .from('voice_ai_sessions')
      .select(
        'id, contact_phone, customer_name, status, service_type, vehicle_model, pickup_address, service_request_id, last_say, updated_at, created_at',
      )
      .order('updated_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message || 'Failed to list sessions');
    return data || [];
  },

  async listJobs(limit = 20) {
    const { data, error } = await supabase
      .from('service_requests')
      .select(
        'id, customer_name, contact_number, service_type, pickup_address, request_status, assigned_mechanic_name, source, requested_at, complaint_registered_at',
      )
      .eq('source', 'voice_ai')
      .order('requested_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message || 'Failed to list Voice AI jobs');
    return data || [];
  },

  async listWaAccounts() {
    const { data, error } = await supabase
      .from('whatsapp_accounts_safe')
      .select('id, name, display_phone, is_default, ai_enabled')
      .order('name');
    if (error) {
      const fallback = await supabase
        .from('whatsapp_accounts')
        .select('id, name, display_phone, is_default')
        .eq('is_active', true);
      if (fallback.error) return [];
      return fallback.data || [];
    }
    return data || [];
  },

  async callTool({ action, phone, field, value, secret, extra = {} }) {
    const res = await fetch(VoiceAiService.brainUrl(), {
      method: 'POST',
      headers: {
        ...restHeaders(),
        'x-voice-ai-secret': secret || '',
      },
      body: JSON.stringify({
        action,
        phone,
        field,
        value,
        admin_test: true,
        ...extra,
      }),
    });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok && body?.success !== false, status: res.status, body };
  },

  async applyOmniDimension({ secret }) {
    const res = await fetch(`${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/omnidim-sync-agent`, {
      method: 'POST',
      headers: {
        ...restHeaders(),
        'x-voice-ai-secret': secret || '',
      },
      body: JSON.stringify({}),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.error || `OmniDimension sync failed (${res.status})`);
    }
    return body;
  },

  recordingUrl({ requestId, callId, list, page, pagesize } = {}) {
    const base = `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1/voice-ai-recording`;
    const params = new URLSearchParams();
    if (list) params.set('list', '1');
    if (page) params.set('page', String(page));
    if (pagesize) params.set('pagesize', String(pagesize));
    if (callId) params.set('call_id', String(callId).trim());
    if (requestId) params.set('request_id', String(requestId).trim());
    const q = params.toString();
    return q ? `${base}?${q}` : base;
  },

  authHeaders() {
    const headers = { apikey: SUPABASE_ANON_KEY };
    try {
      const raw = localStorage.getItem('roadserve_sb_session');
      const backup = raw ? JSON.parse(raw) : null;
      if (backup?.access_token) headers.Authorization = `Bearer ${backup.access_token}`;
    } catch {
      /* anon */
    }
    return headers;
  },

  async withAuthHeaders() {
    const headers = this.authHeaders();
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) {
        headers.Authorization = `Bearer ${data.session.access_token}`;
      }
    } catch {
      /* localStorage token already applied */
    }
    return headers;
  },

  async listCallRecordings({ page = 1, pagesize = 50 } = {}) {
    const headers = await this.withAuthHeaders();
    const res = await fetch(this.recordingUrl({ list: true, page, pagesize }), { headers });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.error || `Failed to list recordings (${res.status})`);
    }
    return {
      calls: Array.isArray(body.calls) ? body.calls : [],
      total: Number(body.total || 0),
      page: Number(body.page || page),
    };
  },

  async fetchRecording(requestIdOrOpts) {
    const opts = typeof requestIdOrOpts === 'string' || typeof requestIdOrOpts === 'number'
      ? { requestId: requestIdOrOpts }
      : (requestIdOrOpts || {});
    const requestId = String(opts.requestId || '').trim();
    const callId = String(opts.callId || '').trim();
    if (!requestId && !callId) return { ok: false, status: 400, error: 'request_id or call_id required' };
    const headers = await this.withAuthHeaders();
    const res = await fetch(this.recordingUrl({ requestId, callId }), { headers });
    const ctype = (res.headers.get('content-type') || '').toLowerCase();
    if (ctype.includes('json') || res.status === 202) {
      const body = await res.json().catch(() => ({}));
      return {
        ok: false,
        status: res.status,
        pending: !!body.pending,
        error: body.error || (res.status === 404 ? 'No Voice AI recording' : 'Recording unavailable'),
        callId: body.call_id || null,
      };
    }
    if (!res.ok) {
      return { ok: false, status: res.status, error: `Recording failed (${res.status})` };
    }
    const blob = await res.blob();
    return {
      ok: true,
      status: 200,
      blob,
      callId: res.headers.get('X-Voice-Ai-Call-Id') || null,
    };
  },
};
